import { ChevronLeft, ChevronRight, List } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { WORKSPACE_TOKENS } from '@/lib/constants';

/**
 * Canonical workspace header. Sticky to centre-pane top, sits above the
 * outline sidebar + lesson scroll container.
 *
 * Hosts: course-outline toggle (far left) + breadcrumb (centre) + Prev/Next
 * lesson arrows (right). The subject segment is rendered as a link when
 * `subjectHref` is provided so users can jump back to the course root
 * without a redundant back button.
 *
 * The Prev/Next arrows form the "sticky breadcrumb-rail" wayfinding contract
 * from the Step 40 Lesson Viewer document model in DESIGN.md: navigation
 * always visible, never scrolls away. Arrows render only when a route
 * publishes `prevLessonHref` / `nextLessonHref` via
 * `useSetWorkspaceBreadcrumb` — non-lesson routes (course-overview, admin
 * editors without sibling-lesson context) leave them off.
 */

export interface WorkspaceHeaderBreadcrumb {
  subject: string;
  subjectHref?: string;
  chapter?: string;
  lesson?: string;
  /** Optional sibling-lesson navigation. Renders ←/→ arrow Links when set. */
  prevLessonHref?: string;
  prevLessonTitle?: string;
  nextLessonHref?: string;
  nextLessonTitle?: string;
}

export interface WorkspaceHeaderProps {
  breadcrumb: WorkspaceHeaderBreadcrumb;
  className?: string;
  outlineOpen?: boolean;
  onToggleOutline?: () => void;
}

function Breadcrumb({ breadcrumb }: { breadcrumb: WorkspaceHeaderBreadcrumb }) {
  const subjectClass = 'hidden shrink-0 truncate md:inline';
  return (
    <nav
      aria-label="Course location"
      className="flex min-w-0 flex-1 items-center gap-1 text-sm text-muted-foreground"
    >
      {breadcrumb.subjectHref ? (
        <Link
          to={breadcrumb.subjectHref}
          data-segment="subject"
          className={cn(
            subjectClass,
            'rounded-sm hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          {breadcrumb.subject}
        </Link>
      ) : (
        <span className={subjectClass} data-segment="subject">
          {breadcrumb.subject}
        </span>
      )}
      {breadcrumb.chapter && (
        <>
          <ChevronRight className="hidden h-3 w-3 shrink-0 md:inline" aria-hidden="true" />
          <span className="hidden shrink truncate sm:inline" data-segment="chapter">
            {breadcrumb.chapter}
          </span>
        </>
      )}
      {breadcrumb.lesson && (
        <>
          <ChevronRight className="hidden h-3 w-3 shrink-0 sm:inline" aria-hidden="true" />
          <span className="min-w-0 truncate font-medium text-foreground" data-segment="lesson">
            {breadcrumb.lesson}
          </span>
        </>
      )}
    </nav>
  );
}

function NavArrows({ breadcrumb }: { breadcrumb: WorkspaceHeaderBreadcrumb }) {
  const { prevLessonHref, prevLessonTitle, nextLessonHref, nextLessonTitle } = breadcrumb;
  if (!prevLessonHref && !nextLessonHref) return null;

  // Same a11y idiom as <LessonDocumentFoot> (Step 41.B.C2): direction-aware
  // aria-labels so SR users hear "Previous lesson: <title>" / "Next lesson:
  // <title>", focus-visible ring for WCAG 2.4.7. No motion: just a color
  // transition on hover/focus, naturally honouring reduced-motion (the
  // universal `*` reduce override caps transition-duration too).
  //
  // Hit area: visual is 32×32 (h-8 w-8) but `-my-2 py-2 -mx-1 px-1` inflates
  // the tap target to ~44×40+ without shifting layout. iOS HIG / Android
  // Material Design / WCAG 2.5.5 (Enhanced) all want ≥44×44 — same trick the
  // sibling LessonDocumentFoot uses on the doc-foot row.
  const linkClass =
    'inline-flex h-8 w-8 -my-2 py-2 -mx-1 px-1 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div
      data-region="breadcrumb-rail-arrows"
      className="flex shrink-0 items-center gap-1"
    >
      {prevLessonHref && (
        <Link
          to={prevLessonHref}
          // Empty title falls back to plain "Previous lesson" rather than
          // emitting a trailing-colon SR string ("Previous lesson: ").
          aria-label={prevLessonTitle ? `Previous lesson: ${prevLessonTitle}` : 'Previous lesson'}
          data-action="prev-lesson"
          className={linkClass}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Link>
      )}
      {nextLessonHref && (
        <Link
          to={nextLessonHref}
          aria-label={nextLessonTitle ? `Next lesson: ${nextLessonTitle}` : 'Next lesson'}
          data-action="next-lesson"
          className={linkClass}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

export function WorkspaceHeader({
  breadcrumb,
  className,
  outlineOpen,
  onToggleOutline,
}: WorkspaceHeaderProps) {
  return (
    <header
      data-chrome="header"
      data-component="workspace-header"
      style={{ height: `${WORKSPACE_TOKENS.WORKSPACE_HEADER_HEIGHT_PX}px` }}
      className={cn(
        'sticky top-0 z-20 flex w-full shrink-0 items-center gap-2 border-b bg-background px-3',
        className,
      )}
    >
      {onToggleOutline && (
        <button
          type="button"
          onClick={onToggleOutline}
          aria-label="Toggle course outline"
          aria-pressed={outlineOpen ?? false}
          data-toggle="outline"
          className={cn(
            'flex h-8 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            outlineOpen && 'bg-muted text-foreground',
          )}
        >
          <List className="h-4 w-4" aria-hidden="true" />
          <span>Content List</span>
        </button>
      )}

      {onToggleOutline && (
        <span aria-hidden="true" className="h-5 w-px bg-border" />
      )}

      <Breadcrumb breadcrumb={breadcrumb} />

      <NavArrows breadcrumb={breadcrumb} />
    </header>
  );
}
